import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAdminAuth } from "auth/AdminAuthProvider";
import {
  createResource,
  listAllResource,
  resourceAction,
  unwrapItems,
  updateResource,
  uploadMedia,
} from "services/adminApi";

const travelStyleOptions = [
  ["adventure_exploration", "مغامرة واستكشاف"],
  ["shopping_entertainment", "تسوق وترفيه"],
  ["history_culture", "تاريخ وثقافة"],
  ["relaxation_wellness", "استرخاء واستجمام"],
];

const placeTypeOptions = [
  ["landmark", "معلم"],
  ["activity", "نشاط"],
  ["restaurant", "مطعم"],
];

const categoryOptions = [
  ["popular", "الأكثر طلباً"],
  ["europe", "أوروبا"],
  ["asia", "آسيا"],
  ["beach", "شواطئ وجزر"],
  ["nature", "طبيعة ومغامرة"],
  ["culture", "ثقافة وتاريخ"],
];

const blankCountry = () => ({
  name: "",
  displayName: "",
  category: "popular",
  travelStyles: [],
  heroImage: "",
  tagline: "",
  countryGuide: { title: "", introduction: "" },
  entryCheck: {
    summary: "",
    visaLabel: "",
    requirements: [],
    officialSources: [],
    verifiedAt: "",
    verificationStatus: "unavailable",
  },
});

function effective(item) {
  return item?.draft || item?.effective || item || {};
}

function recordId(item, field) {
  return item?.entityId || effective(item)?.[field];
}

function statusLabel(status) {
  return (
    {
      draft: "مسودة",
      review: "للمراجعة",
      published: "منشور",
      archived: "مؤرشف",
    }[status || "published"] || status
  );
}

function statusColor(status) {
  return { draft: "warning", review: "info", published: "success", archived: "default" }[
    status || "published"
  ];
}

function CountriesPage() {
  const auth = useAdminAuth();
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [places, setPlaces] = useState([]);
  const [notices, setNotices] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [countryDialog, setCountryDialog] = useState(null);
  const [cityDialog, setCityDialog] = useState(null);
  const [placeDialog, setPlaceDialog] = useState(null);
  const [noticeDialog, setNoticeDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState("");

  const countryDialogId = recordId(countryDialog?.source, "countryId");
  const countryDialogOpen = Boolean(countryDialog);
  useEffect(() => {
    setUploadInfo("");
  }, [countryDialogId, countryDialogOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [countryPayload, cityPayload, placePayload, noticePayload] = await Promise.all([
        listAllResource("countries"),
        listAllResource("destinations"),
        listAllResource("places"),
        listAllResource("notices"),
      ]);
      setCountries(unwrapItems(countryPayload));
      setCities(unwrapItems(cityPayload));
      setPlaces(unwrapItems(placePayload));
      setNotices(unwrapItems(noticePayload));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visibleCountries = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return countries;
    return countries.filter((item) => {
      const country = effective(item);
      return `${country.name || ""} ${country.displayName || ""}`.toLowerCase().includes(term);
    });
  }, [countries, search]);
  const selected = countries.find((item) => recordId(item, "countryId") === selectedId) || null;
  const selectedCountry = effective(selected);
  const selectedCountryId = recordId(selected, "countryId");
  const countryCities = selected
    ? cities.filter((item) => {
        const city = effective(item);
        return city.countryId === selectedCountryId || city.country === selectedCountry.name;
      })
    : [];
  const countryNotices = selected
    ? notices.filter((item) => item.country === selectedCountry.name && item.scope === "country")
    : [];

  const saveCountry = async () => {
    if (!countryDialog || saving) return;
    setSaving(true);
    try {
      const id = recordId(countryDialog.source, "countryId");
      const saved = id
        ? await updateResource("countries", id, {
            ...countryDialog.values,
            expectedVersion: countryDialog.source?.version,
          })
        : await createResource("countries", countryDialog.values);
      setCountryDialog({ source: saved, values: effective(saved) });
      setSelectedId(recordId(saved, "countryId"));
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadCountryImage = async (event) => {
    const file = event.target.files?.[0];
    const id = recordId(countryDialog?.source, "countryId");
    if (!file || !id) return;
    setUploading(true);
    setUploadInfo("");
    try {
      const uploaded = await uploadMedia({ file, entityType: "countries", entityId: id });
      setCountryDialog((current) => ({
        ...current,
        values: { ...current.values, heroImage: uploaded.publicUrl },
      }));
      setUploadInfo(uploaded.optimization?.summary || "تم رفع الصورة بنجاح.");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setUploading(false);
    }
  };

  const saveCity = async () => {
    if (!cityDialog || saving) return;
    setSaving(true);
    try {
      await createResource("destinations", cityDialog.values);
      setCityDialog(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const savePlace = async () => {
    if (!placeDialog || saving) return;
    setSaving(true);
    try {
      await createResource("places", placeDialog.values);
      setPlaceDialog(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const saveNotice = async () => {
    if (!noticeDialog || saving) return;
    setSaving(true);
    try {
      await createResource("notices", noticeDialog.values);
      setNoticeDialog(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const act = async (resource, item, action) => {
    const idField =
      resource === "countries"
        ? "countryId"
        : resource === "destinations"
        ? "destinationId"
        : "noticeId";
    const id = recordId(item, idField);
    if (
      !id ||
      !window.confirm(
        action === "publish" ? "هل تريد نشر هذا المحتوى للمسافرين؟" : "هل تريد أرشفة هذا السجل؟"
      )
    )
      return;
    try {
      await resourceAction(resource, id, action, {
        environment: process.env.REACT_APP_ENVIRONMENT || "dev",
        confirm: true,
      });
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const openNewCity = () => {
    if (!selected) return;
    setCityDialog({
      values: {
        countryId: selectedCountryId,
        country: selectedCountry.name,
        area: "",
        displayName: "",
        heroImage: "",
        tagline: "",
      },
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} dir="rtl">
        <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
          <MDBox>
            <MDTypography variant="h4" fontWeight="bold">
              الدول والمدن
            </MDTypography>
            <MDTypography variant="button" color="text">
              معلومات السفر والصورة والدليل للدولة، ثم المدن بداخلها، ثم الأماكن داخل كل مدينة.
            </MDTypography>
          </MDBox>
          <Button
            variant="contained"
            startIcon={<Icon>add</Icon>}
            sx={{ bgcolor: "#179B82", "&:hover": { bgcolor: "#12806B" } }}
            onClick={() => setCountryDialog({ source: null, values: blankCountry() })}
          >
            إضافة دولة
          </Button>
        </MDBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {loading ? (
          <MDBox py={10} textAlign="center">
            <CircularProgress color="success" />
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <MDBox bgColor="white" shadow="sm" borderRadius="xl" p={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="ابحث عن دولة"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ mb: 2 }}
                />
                {visibleCountries.map((item) => {
                  const country = effective(item);
                  const id = recordId(item, "countryId");
                  const active = id === selectedCountryId;
                  return (
                    <MDBox
                      key={id}
                      onClick={() => setSelectedId(id)}
                      role="button"
                      tabIndex={0}
                      sx={{
                        cursor: "pointer",
                        p: 1.5,
                        mb: 1,
                        borderRadius: "lg",
                        border: active ? "1px solid #179B82" : "1px solid #E1ECEF",
                        backgroundColor: active ? "#F2FBF8" : "transparent",
                      }}
                    >
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        gap={1}
                        alignItems="center"
                      >
                        <MDBox minWidth={0}>
                          <MDTypography variant="button" fontWeight="bold" display="block">
                            {country.displayName || country.name}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {country.citiesCount || 0} مدن
                          </MDTypography>
                        </MDBox>
                        <Chip
                          size="small"
                          label={statusLabel(item.status)}
                          color={statusColor(item.status)}
                        />
                      </MDBox>
                    </MDBox>
                  );
                })}
                {visibleCountries.length === 0 && (
                  <MDTypography color="text">لا توجد دول مطابقة.</MDTypography>
                )}
              </MDBox>
            </Grid>

            <Grid item xs={12} md={8}>
              {!selected ? (
                <MDBox bgColor="white" shadow="sm" borderRadius="xl" p={5} textAlign="center">
                  <Icon sx={{ fontSize: 52, color: "#9CB0B8" }}>public</Icon>
                  <MDTypography color="text">اختر دولة لعرض مدنها وتنبيهاتها.</MDTypography>
                </MDBox>
              ) : (
                <MDBox bgColor="white" shadow="sm" borderRadius="xl" p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1.5}
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h5" fontWeight="bold">
                        {selectedCountry.displayName || selectedCountry.name}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        معرّف الدولة: {selectedCountryId}
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={1} flexWrap="wrap">
                      <Button
                        size="small"
                        startIcon={<Icon>edit</Icon>}
                        onClick={() =>
                          setCountryDialog({ source: selected, values: effective(selected) })
                        }
                      >
                        تعديل معلومات الدولة
                      </Button>
                      {auth.canPublish && selected.status !== "published" && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => act("countries", selected, "publish")}
                        >
                          نشر الدولة
                        </Button>
                      )}
                    </MDBox>
                  </MDBox>
                  <MDTypography variant="button" color="text" display="block" mb={2}>
                    {selectedCountry.tagline || "أكمل ملخص الدولة لتقديمه للمسافر."}
                  </MDTypography>

                  <Divider sx={{ my: 2 }} />
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    mb={1.5}
                  >
                    <MDTypography variant="h6" fontWeight="bold">
                      المدن
                    </MDTypography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon>add_location_alt</Icon>}
                      onClick={openNewCity}
                    >
                      إضافة مدينة
                    </Button>
                  </MDBox>
                  {countryCities.length === 0 ? (
                    <MDTypography color="text" variant="button">
                      لا توجد مدن بعد. أضف أول مدينة لهذه الدولة.
                    </MDTypography>
                  ) : (
                    countryCities.map((item) => {
                      const city = effective(item);
                      const cityId = recordId(item, "destinationId");
                      const cityPlaces = places.filter(
                        (place) => effective(place).destinationId === cityId
                      );
                      return (
                        <MDBox
                          key={cityId}
                          p={1.5}
                          mb={1}
                          border="1px solid #E1ECEF"
                          borderRadius="lg"
                        >
                          <MDBox
                            display="flex"
                            justifyContent="space-between"
                            gap={1}
                            flexWrap="wrap"
                          >
                            <MDBox>
                              <MDTypography variant="button" fontWeight="bold">
                                {city.area || city.displayName}
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                {cityPlaces.length} أماكن مرتبطة بهذه المدينة
                              </MDTypography>
                            </MDBox>
                            <MDBox display="flex" gap={1} flexWrap="wrap">
                              <Chip
                                size="small"
                                label={statusLabel(item.status)}
                                color={statusColor(item.status)}
                              />
                              <Button
                                size="small"
                                onClick={() =>
                                  setPlaceDialog({
                                    city,
                                    values: {
                                      destinationId: cityId,
                                      nameAr: "",
                                      nameEn: "",
                                      placeType: "landmark",
                                      descriptionAr: "",
                                      mapUrl: "",
                                    },
                                  })
                                }
                              >
                                إضافة مكان
                              </Button>
                              {auth.canPublish && item.status !== "published" && (
                                <Button
                                  size="small"
                                  color="success"
                                  onClick={() => act("destinations", item, "publish")}
                                >
                                  نشر
                                </Button>
                              )}
                            </MDBox>
                          </MDBox>
                          {cityPlaces.length > 0 && (
                            <MDBox display="flex" gap={0.75} flexWrap="wrap" mt={1}>
                              {cityPlaces.map((place) => (
                                <Chip
                                  key={recordId(place, "placeId")}
                                  size="small"
                                  variant="outlined"
                                  label={`${effective(place).nameAr} · ${
                                    placeTypeOptions.find(
                                      ([id]) => id === effective(place).placeType
                                    )?.[1] || "مكان"
                                  }`}
                                />
                              ))}
                            </MDBox>
                          )}
                        </MDBox>
                      );
                    })
                  )}

                  <Divider sx={{ my: 2.5 }} />
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    mb={1.5}
                  >
                    <MDTypography variant="h6" fontWeight="bold">
                      تنبيهات خاصة بالدولة
                    </MDTypography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Icon>campaign</Icon>}
                      onClick={() =>
                        setNoticeDialog({
                          values: {
                            title: "",
                            message: "",
                            scope: "country",
                            country: selectedCountry.name,
                            priority: "normal",
                            startAt: "",
                            endAt: "",
                          },
                        })
                      }
                    >
                      إضافة تنبيه
                    </Button>
                  </MDBox>
                  {countryNotices.length === 0 ? (
                    <MDTypography color="text" variant="button">
                      لا توجد تنبيهات خاصة بهذه الدولة.
                    </MDTypography>
                  ) : (
                    countryNotices.map((notice) => (
                      <MDBox
                        key={notice.noticeId}
                        display="flex"
                        justifyContent="space-between"
                        gap={1}
                        p={1.25}
                        mb={1}
                        border="1px solid #E1ECEF"
                        borderRadius="lg"
                      >
                        <MDBox>
                          <MDTypography variant="button" fontWeight="bold">
                            {notice.title}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {notice.message}
                          </MDTypography>
                        </MDBox>
                        <MDBox display="flex" gap={1} alignItems="center">
                          <Chip
                            size="small"
                            label={statusLabel(notice.status)}
                            color={statusColor(notice.status)}
                          />
                          {auth.canPublish && notice.status !== "published" && (
                            <Button
                              size="small"
                              color="success"
                              onClick={() => act("notices", notice, "publish")}
                            >
                              نشر
                            </Button>
                          )}
                        </MDBox>
                      </MDBox>
                    ))
                  )}
                </MDBox>
              )}
            </Grid>
          </Grid>
        )}
      </MDBox>

      <CountryDialog
        open={Boolean(countryDialog)}
        state={countryDialog}
        setState={setCountryDialog}
        onClose={() => !saving && setCountryDialog(null)}
        onSave={saveCountry}
        uploading={uploading}
        uploadInfo={uploadInfo}
        onUpload={uploadCountryImage}
        saving={saving}
      />
      <CityDialog
        open={Boolean(cityDialog)}
        state={cityDialog}
        setState={setCityDialog}
        onClose={() => !saving && setCityDialog(null)}
        onSave={saveCity}
        saving={saving}
      />
      <PlaceDialog
        open={Boolean(placeDialog)}
        state={placeDialog}
        setState={setPlaceDialog}
        onClose={() => !saving && setPlaceDialog(null)}
        onSave={savePlace}
        saving={saving}
      />
      <NoticeDialog
        open={Boolean(noticeDialog)}
        state={noticeDialog}
        setState={setNoticeDialog}
        onClose={() => !saving && setNoticeDialog(null)}
        onSave={saveNotice}
        saving={saving}
      />
    </DashboardLayout>
  );
}

function CountryDialog({
  open,
  state,
  setState,
  onClose,
  onSave,
  uploading,
  uploadInfo,
  onUpload,
  saving,
}) {
  const values = state?.values || blankCountry();
  const guide = values.countryGuide || {};
  const entryCheck = values.entryCheck || {};
  const [tab, setTab] = useState(0);
  const update = (key, value) =>
    setState((current) => ({ ...current, values: { ...current.values, [key]: value } }));
  const updateGuide = (key, value) => update("countryGuide", { ...guide, [key]: value });
  const updateEntryCheck = (key, value) => update("entryCheck", { ...entryCheck, [key]: value });
  const id = recordId(state?.source, "countryId");
  const sections = [
    "الهوية والمظهر",
    "معلومات السفر",
    "العائلة والميزانية",
    "نصائح المسافر",
    "الدليل ومتطلبات الدخول",
    "فحص دخول الدول",
  ];

  useEffect(() => {
    if (open) setTab(0);
  }, [open, id]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" dir="rtl">
      <DialogTitle>
        {id ? "تعديل معلومات الدولة" : "إضافة دولة"}
        <MDTypography variant="caption" color="text" display="block" mt={0.5}>
          القسم {tab + 1} من {sections.length}: {sections[tab]}
        </MDTypography>
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ px: 3 }}
      >
        {sections.map((section) => (
          <Tab key={section} label={section} />
        ))}
      </Tabs>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          هذه معلومات الدولة الكاملة. المدن والمعالم والأنشطة والمطاعم تدار من القسم التابع لها، ولا
          تُكرر هنا.
        </Alert>
        {tab === 0 && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="اسم الدولة"
                value={values.name || ""}
                onChange={(e) => update("name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="الاسم الظاهر"
                value={values.displayName || ""}
                onChange={(e) => update("displayName", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="التصنيف"
                value={values.category || "popular"}
                onChange={(e) => update("category", e.target.value)}
              >
                {categoryOptions.map(([idOption, label]) => (
                  <MenuItem key={idOption} value={idOption}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                select
                label="أنماط السفر"
                value={values.travelStyles || []}
                SelectProps={{ multiple: true }}
                onChange={(e) => update("travelStyles", e.target.value)}
              >
                {travelStyleOptions.map(([idOption, label]) => (
                  <MenuItem key={idOption} value={idOption}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="رابط صورة غلاف الدولة"
                value={values.heroImage || ""}
                onChange={(e) => update("heroImage", e.target.value)}
              />
            </Grid>
            {id && (
              <Grid item xs={12}>
                <Button component="label" variant="outlined" disabled={uploading}>
                  {uploading ? "جارٍ رفع الصورة..." : "رفع صورة غلاف"}
                  <input
                    hidden
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={onUpload}
                  />
                </Button>
                {uploadInfo && (
                  <MDTypography variant="caption" color="success" display="block" mt={1}>
                    {uploadInfo}
                  </MDTypography>
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                label="الوصف المختصر"
                value={values.tagline || ""}
                onChange={(e) => update("tagline", e.target.value)}
              />
            </Grid>
          </Grid>
        )}
        {tab === 1 && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="العملة"
                value={values.currency || ""}
                onChange={(e) => update("currency", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="اللغة"
                value={values.language || ""}
                onChange={(e) => update("language", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="ملخص التنقل في الدولة"
                value={values.transportSummary || ""}
                onChange={(e) => update("transportSummary", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LinesField
                label="أفضل وقت للزيارة"
                value={values.bestTime}
                onChange={(value) => update("bestTime", value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LinesField
                label="أبرز مناطق الدولة"
                value={values.attractions}
                onChange={(value) => update("attractions", value)}
              />
            </Grid>
          </Grid>
        )}
        {tab === 2 && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="تقييم ملاءمة العائلات"
                value={values.familyRating || ""}
                onChange={(e) => update("familyRating", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LinesField
                label="وسوم الأعمار"
                value={values.ageTags}
                onChange={(value) => update("ageTags", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="سبب ملاءمة الدولة للعائلات"
                value={values.familyReasons || ""}
                onChange={(e) => update("familyReasons", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="ملاحظة مهمة للمسافر"
                value={values.adminNotice || ""}
                onChange={(e) => update("adminNotice", e.target.value)}
              />
            </Grid>
          </Grid>
        )}
        {tab === 3 && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <LinesField
                label="نصائح التنقل"
                value={values.transportTips}
                onChange={(value) => update("transportTips", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <LinesField
                label="السلامة والثقافة"
                value={values.safetyCultureTips}
                onChange={(value) => update("safetyCultureTips", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <LinesField
                label="نصائح المسافر"
                value={values.travelerTips}
                onChange={(value) => update("travelerTips", value)}
              />
            </Grid>
          </Grid>
        )}
        {tab === 4 && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="عنوان دليل الدولة"
                value={guide.title || ""}
                onChange={(e) => updateGuide("title", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="العنوان الافتتاحي"
                value={guide.headline || ""}
                onChange={(e) => updateGuide("headline", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="مقدمة الدليل"
                value={guide.introduction || ""}
                onChange={(e) => updateGuide("introduction", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="عن الدولة"
                value={guide.aboutCountry || ""}
                onChange={(e) => updateGuide("aboutCountry", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LinesField
                label="بيانات سريعة للدليل"
                value={guide.metadata}
                onChange={(value) => updateGuide("metadata", value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LinesField
                label="تجارب مقترحة"
                value={guide.experiences}
                onChange={(value) => updateGuide("experiences", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <GuideItemsField
                label="متطلبات الدخول"
                value={guide.entryRequirements}
                onChange={(value) => updateGuide("entryRequirements", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="تنبيه متطلبات الدخول"
                value={guide.entryDisclaimer || ""}
                onChange={(e) => updateGuide("entryDisclaimer", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GuideSourcesField
                label="المصادر"
                value={guide.sources}
                onChange={(value) => updateGuide("sources", value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="آخر تحقق"
                value={guide.verifiedAt || ""}
                onChange={(e) => updateGuide("verifiedAt", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <LinesField
                label="نصائح إضافية في الدليل"
                value={guide.tips}
                onChange={(value) => updateGuide("tips", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="ملخص الميزانية في الدليل"
                value={guide.budget || ""}
                onChange={(e) => updateGuide("budget", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <LinesField
                label="تمهيد المصادر"
                value={guide.sourcePreamble}
                onChange={(value) => updateGuide("sourcePreamble", value)}
              />
            </Grid>
          </Grid>
        )}
        {tab === 5 && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <Alert severity="warning">
                لا تظهر نتيجة موثّقة في التطبيق إلا بعد اختيار «موثّق»، إكمال المصدر الرسمي وتاريخ
                التحقق، ثم حفظ المسودة ونشر الدولة من صفحة الدول.
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="حالة التحقق"
                value={entryCheck.verificationStatus || "unavailable"}
                onChange={(e) => updateEntryCheck("verificationStatus", e.target.value)}
              >
                <MenuItem value="unavailable">لا تتوفر نتيجة موثّقة</MenuItem>
                <MenuItem value="review_required">بحاجة إلى إعادة تحقق</MenuItem>
                <MenuItem value="verified">موثّق من مصدر رسمي</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="تاريخ آخر تحقق"
                value={entryCheck.verifiedAt || ""}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => updateEntryCheck("verifiedAt", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="النتيجة المختصرة"
                placeholder="مثال: بدون تأشيرة"
                value={entryCheck.visaLabel || ""}
                onChange={(e) => updateEntryCheck("visaLabel", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="ملخص النتيجة"
                value={entryCheck.summary || ""}
                onChange={(e) => updateEntryCheck("summary", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <GuideItemsField
                label="متطلبات الدخول السياحي"
                value={entryCheck.requirements}
                onChange={(value) => updateEntryCheck("requirements", value)}
              />
            </Grid>
            <Grid item xs={12}>
              <GuideSourcesField
                label="المصادر الحكومية الرسمية"
                value={entryCheck.officialSources}
                onChange={(value) => updateEntryCheck("officialSources", value)}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {tab > 0 && <Button onClick={() => setTab((current) => current - 1)}>السابق</Button>}
        {tab < sections.length - 1 && (
          <Button onClick={() => setTab((current) => current + 1)}>التالي</Button>
        )}
        <Button onClick={onClose}>إلغاء</Button>
        <Button variant="contained" onClick={onSave} disabled={saving} sx={{ bgcolor: "#179B82" }}>
          {saving ? "جارٍ الحفظ..." : "حفظ كمسودة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LinesField({ label, value, onChange }) {
  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      label={label}
      value={(value || []).join("\n")}
      helperText="اكتب كل عنصر في سطر مستقل."
      onChange={(event) => onChange(event.target.value.split("\n").filter(Boolean))}
    />
  );
}

function GuideItemsField({ label, value, onChange }) {
  const lines = (value || [])
    .map((item) => `${item?.title || ""} | ${item?.description || ""}`.trim())
    .join("\n");
  return (
    <TextField
      fullWidth
      multiline
      minRows={4}
      label={label}
      value={lines}
      helperText="كل سطر: العنوان | الوصف"
      onChange={(event) =>
        onChange(
          event.target.value
            .split("\n")
            .filter(Boolean)
            .map((line) => {
              const [title, ...description] = line.split("|");
              return { title: title.trim(), description: description.join("|").trim() };
            })
            .filter((item) => item.title)
        )
      }
    />
  );
}

function GuideSourcesField({ label, value, onChange }) {
  const lines = (value || [])
    .map((item) => `${item?.label || ""} | ${item?.url || ""}`.trim())
    .join("\n");
  return (
    <TextField
      fullWidth
      multiline
      minRows={4}
      label={label}
      value={lines}
      helperText="كل سطر: اسم المصدر | الرابط"
      onChange={(event) =>
        onChange(
          event.target.value
            .split("\n")
            .filter(Boolean)
            .map((line) => {
              const [labelValue, ...url] = line.split("|");
              return { label: labelValue.trim(), url: url.join("|").trim() };
            })
            .filter((item) => item.label || item.url)
        )
      }
    />
  );
}

LinesField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};
LinesField.defaultProps = { value: [] };
GuideItemsField.propTypes = LinesField.propTypes;
GuideItemsField.defaultProps = { value: [] };
GuideSourcesField.propTypes = LinesField.propTypes;
GuideSourcesField.defaultProps = { value: [] };

function CityDialog({ open, state, setState, onClose, onSave, saving }) {
  const values = state?.values || {};
  const update = (key, value) =>
    setState((current) => ({ ...current, values: { ...current.values, [key]: value } }));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir="rtl">
      <DialogTitle>إضافة مدينة إلى {values.country}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12}>
            <TextField fullWidth disabled label="الدولة الأم" value={values.country || ""} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="اسم المدينة"
              value={values.area || ""}
              onChange={(e) => {
                const area = e.target.value;
                update("area", area);
                update("displayName", `${values.country} - ${area}`);
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="الاسم الظاهر"
              value={values.displayName || ""}
              onChange={(e) => update("displayName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="رابط صورة المدينة (اختياري)"
              value={values.heroImage || ""}
              onChange={(e) => update("heroImage", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="وصف المدينة المختصر (اختياري)"
              value={values.tagline || ""}
              onChange={(e) => update("tagline", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button variant="contained" onClick={onSave} disabled={saving} sx={{ bgcolor: "#179B82" }}>
          {saving ? "جارٍ الحفظ..." : "حفظ المدينة كمسودة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlaceDialog({ open, state, setState, onClose, onSave, saving }) {
  const values = state?.values || {};
  const update = (key, value) =>
    setState((current) => ({ ...current, values: { ...current.values, [key]: value } }));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir="rtl">
      <DialogTitle>إضافة مكان إلى {state?.city?.area || "المدينة"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              disabled
              label="المدينة الأم"
              value={state?.city?.displayName || ""}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="اسم المكان بالعربية"
              value={values.nameAr || ""}
              onChange={(e) => update("nameAr", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="الاسم بالإنجليزية"
              value={values.nameEn || ""}
              onChange={(e) => update("nameEn", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              select
              label="نوع المكان"
              value={values.placeType || "landmark"}
              onChange={(e) => update("placeType", e.target.value)}
            >
              {placeTypeOptions.map(([idOption, label]) => (
                <MenuItem key={idOption} value={idOption}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="الوصف"
              value={values.descriptionAr || ""}
              onChange={(e) => update("descriptionAr", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="رابط الخريطة"
              value={values.mapUrl || ""}
              onChange={(e) => update("mapUrl", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button variant="contained" onClick={onSave} disabled={saving} sx={{ bgcolor: "#179B82" }}>
          {saving ? "جارٍ الحفظ..." : "حفظ المكان كمسودة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NoticeDialog({ open, state, setState, onClose, onSave, saving }) {
  const values = state?.values || {};
  const update = (key, value) =>
    setState((current) => ({ ...current, values: { ...current.values, [key]: value } }));
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" dir="rtl">
      <DialogTitle>تنبيه خاص بـ {values.country}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} mt={0}>
          <Grid item xs={12}>
            <TextField fullWidth disabled label="الدولة" value={values.country || ""} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="عنوان التنبيه"
              value={values.title || ""}
              onChange={(e) => update("title", e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              minRows={3}
              label="رسالة التنبيه"
              value={values.message || ""}
              onChange={(e) => update("message", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="الأولوية"
              value={values.priority || "normal"}
              onChange={(e) => update("priority", e.target.value)}
            >
              <MenuItem value="normal">عادية</MenuItem>
              <MenuItem value="high">مرتفعة</MenuItem>
              <MenuItem value="urgent">عاجلة</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="datetime-local"
              label="نهاية الظهور (اختياري)"
              InputLabelProps={{ shrink: true }}
              value={values.endAt || ""}
              onChange={(e) => update("endAt", e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>إلغاء</Button>
        <Button variant="contained" onClick={onSave} disabled={saving} sx={{ bgcolor: "#179B82" }}>
          {saving ? "جارٍ الحفظ..." : "حفظ التنبيه كمسودة"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const dialogPropTypes = {
  open: PropTypes.bool.isRequired,
  state: PropTypes.object,
  setState: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

CountryDialog.propTypes = {
  ...dialogPropTypes,
  uploading: PropTypes.bool.isRequired,
  uploadInfo: PropTypes.string.isRequired,
  onUpload: PropTypes.func.isRequired,
};
CountryDialog.defaultProps = { state: null };
CityDialog.propTypes = dialogPropTypes;
CityDialog.defaultProps = { state: null };
PlaceDialog.propTypes = dialogPropTypes;
PlaceDialog.defaultProps = { state: null };
NoticeDialog.propTypes = dialogPropTypes;
NoticeDialog.defaultProps = { state: null };

export default CountriesPage;
